import { useEffect, useRef, useState } from 'react';
import { FiArrowLeft, FiArrowRight, FiMaximize2, FiX } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { trackEvent } from '../lib/tracking';

export default function PropertyGallery({ images }) {
  const [active, setActive] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const touchStart = useRef(null);

  useEffect(() => {
    if (!fullscreen) return undefined;
    const closeOnEscape = (event) => event.key === 'Escape' && setFullscreen(false);
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [fullscreen]);

  const move = (direction) => {
    if (!images.length) return;
    setActive((index) => (index + direction + images.length) % images.length);
    trackEvent('GalleryInteraction', { action: direction > 0 ? 'next' : 'previous' });
  };

  const swipeStart = (event) => {
    touchStart.current = event.touches[0].clientX;
  };

  const swipeEnd = (event) => {
    if (touchStart.current === null) return;
    const distance = event.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(distance) > 45) move(distance < 0 ? 1 : -1);
    touchStart.current = null;
  };

  if (!images.length) {
    return (
      <section className="section gallery-section" id="photos">
        <div className="section-heading">
          <p className="eyebrow">A closer look</p>
          <h2>Explore the home</h2>
          <p>Authorized property photos can be added in the property configuration.</p>
        </div>
        <div className="gallery-empty" role="img" aria-label="Property photography will appear here when authorized images are added">
          <div className="empty-art"><span>3649</span><i /></div>
          <p>Property photography coming soon</p>
        </div>
      </section>
    );
  }

  const image = images[active];
  return (
    <section className="section gallery-section" id="photos">
      <div className="section-heading">
        <p className="eyebrow">A closer look</p>
        <h2>Explore the home</h2>
      </div>
      <div className="gallery-stage" onTouchStart={swipeStart} onTouchEnd={swipeEnd}>
        <img key={image.src} src={image.src} alt={image.alt || image.caption || 'Property photo'} loading="lazy" />
        <span className="gallery-caption">{image.caption || 'Property photo'}</span>
        <button className="gallery-control previous" onClick={() => move(-1)} aria-label="Previous photo">
          <SafeIcon icon={FiArrowLeft} />
        </button>
        <button className="gallery-control next" onClick={() => move(1)} aria-label="Next photo">
          <SafeIcon icon={FiArrowRight} />
        </button>
        <button className="gallery-control fullscreen" onClick={() => setFullscreen(true)} aria-label="View photo fullscreen">
          <SafeIcon icon={FiMaximize2} />
        </button>
      </div>
      <div className="gallery-thumbnails" aria-label="Choose a property photo">
        {images.map((item, index) => (
          <button className={active === index ? 'thumbnail active' : 'thumbnail'} key={item.src} onClick={() => setActive(index)} aria-label={`Show ${item.caption || `photo ${index + 1}`}`}>
            <img src={item.src} alt="" loading="lazy" />
          </button>
        ))}
      </div>
      {fullscreen && (
        <div className="lightbox" role="dialog" aria-modal="true" aria-label="Fullscreen property photo" onClick={() => setFullscreen(false)}>
          <button className="lightbox-close" onClick={() => setFullscreen(false)} aria-label="Close fullscreen photo"><SafeIcon icon={FiX} /></button>
          <img src={image.src} alt={image.alt || image.caption || 'Property photo'} onClick={(event) => event.stopPropagation()} />
          <button className="gallery-control previous" onClick={(event) => { event.stopPropagation(); move(-1); }} aria-label="Previous photo"><SafeIcon icon={FiArrowLeft} /></button>
          <button className="gallery-control next" onClick={(event) => { event.stopPropagation(); move(1); }} aria-label="Next photo"><SafeIcon icon={FiArrowRight} /></button>
        </div>
      )}
    </section>
  );
}